import React, { useEffect, useReducer } from "react";
import { Typography, TextField, InputAdornment } from "@mui/material";

import { withRouter } from "react-router";
import { compose } from "underscore";
import { connect } from "react-redux";
import { CATEGORY_SAVING, GENERAL_SAVING } from "../redux/constant";
import { setFullRedemption, checkWithdrawalMaxAmount } from "../redux/action";

import {
  StepLabel,
  CustomButton,
  NumberFormatCustom,
  DefaultLoader,
  Alert,
  ButtonWrapper,
} from "../../components";
import { initState, reducer } from "./reducer";
import { sendFundsCode } from "../fundSelection/redux/action";
import { getRoundedValue } from "../../utils/functions/getRoundedValue";
import { CashOutInfo, Container, Form } from "./styles";

const getTotalAsset = ({ unitHeld, nav }) => {
  return unitHeld * nav;
};

const MakeWithdrawal = props => {
  const {
    loading,
    savingType,
    savingGoalDetails,
    generalSavingDetails,
    customerGoals,
    checkWithdrawalMaxAmount,
    maxAmount,
    history,
    loadingMaxAmount,
    fund,
    sendFundsCode,
    setFullRedemption,
  } = props;

  const [state, dispatch] = useReducer(reducer, initState);

  const isSave = savingGoalDetails?.id || generalSavingDetails?.id;

  const isInvest = fund?.product?.productCatCd === "MPInvest";

  /** Save saving goal data */
  useEffect(() => {
    if (savingType === CATEGORY_SAVING && savingGoalDetails) {
      handleSetGoal(savingGoalDetails);
    }
  }, [savingType, savingGoalDetails]);

  useEffect(() => {
    if (savingType === GENERAL_SAVING && generalSavingDetails) {
      handleSetGoal(generalSavingDetails);
    }
  }, [savingType, generalSavingDetails]);

  useEffect(() => {
    if (isSave) return;

    if (isInvest) {
      checkWithdrawalMaxAmount(`${fund.cifPlanId}?fundCd=${fund.code}`);
    }
  }, [fund, isInvest, isSave]);

  /**
   *
   * @param {Record<string, unknown>} goal
   */
  const handleSetGoal = goal => {
    if (goal) {
      sendFundsCode([]);

      const saving = customerGoals.find(item => item.id === goal.id);

      dispatch({ type: "SET_GOAL", payload: saving });

      checkWithdrawalMaxAmount(goal.id);
    }
  };

  /**
   *
   * @param {number} totalAsset
   */
  const handleFullRedemption = totalAsset => {
    // add guard incase max amount api is not deployed to production
    // if not deployed, it will takes the goals remaining total assets
    if (maxAmount && maxAmount.totalAmount) {
      if (maxAmount.totalAmount >= 0) {
        const amount = getRoundedValue(maxAmount.totalAmount, 2);

        dispatch({ type: "SET_WITHDRAWAL_AMOUNT", payload: amount.toString() });
      } else {
        dispatch({
          type: "RESET_WITHDRAWAL_AMOUNT",
        });
      }
    } else {
      const amount = getRoundedValue(totalAsset, 2);

      if (amount < state.saveMinAmount || amount < state.investMinAmount) {
        dispatch({ type: "SET_FULL_REDEMPTION_ERROR" });
      } else {
        dispatch({
          type: "SET_WITHDRAWAL_AMOUNT",
          payload: amount.toString(),
        });
      }
    }
  };

  const handleContinue = () => {
    let totalAsset;

    if (isInvest) {
      totalAsset = getTotalAsset({
        nav: fund.nav,
        unitHeld: fund.unitHeld,
      });
    } else {
      totalAsset = getTotalAsset({
        nav: state.savingDetail.portfolio.unitHeld,
        unitHeld: state.savingDetail.portfolio.fund.nav,
      });
    }

    const amount = getRoundedValue(
      maxAmount ? maxAmount.totalAmount : totalAsset,
      2
    );

    if (
      amount - Number(state.withdrawalAmount) > 0 &&
      (amount - Number(state.withdrawalAmount) < state.saveMinAmount ||
        amount - Number(state.withdrawalAmount) < state.investMinAmount)
    ) {
      dispatch({ type: "RESET_OPEN_ALERT_AND_REDEMPTION_ERROR" });
      handleFullRedemption(totalAsset);
      return;
    }

    // inputted amount is less than or same as total amount
    // allow
    if (Number(state.withdrawalAmount) <= amount) {
      if (amount - Number(state.withdrawalAmount) === 0) {
        setFullRedemption(true);
        history.push(`/make-withdrawal/summary/${amount}`);
      } else {
        setFullRedemption(false);
        history.push(`/make-withdrawal/summary/${state.withdrawalAmount}`);
      }
    } else {
      if (maxAmount && maxAmount.totalAmount) {
        dispatch({ type: "RESET_OPEN_ALERT_AND_SET_LOW_BALANCE_ERROR" });
        handleFullRedemption(totalAsset);
      } else {
        dispatch({ type: "RESET_OPEN_ALERT_SET_CASHOUT_ERROR" });
      }
    }
  };

  const handleBack = () => {
    setFullRedemption(false);
    history.back();
  };

  if (loadingMaxAmount || loading) return <DefaultLoader />;

  return (
    <Container>
      <Alert
        title="Are you sure you want to Cash Out?"
        description="You might delay on achieving your goal."
        isOpen={state.isOpenAlert}
        isCancel
        handleClose={() => dispatch({ type: "RESET_OPEN_ALERT" })}
        handleAction={handleContinue}
        handleCancel={() => dispatch({ type: "RESET_OPEN_ALERT" })}
        btnTxt="Continue"
        swapButtonsPosition
        submitButtonStyle={{
          marginTop: "14px",
        }}
      />
      <Alert
        title="Ops, insufficient balance for Cash Out"
        description="Your financial goal has insufficient balance to cash out."
        isOpen={state.isCashOutError}
        isCancel={false}
        handleClose={() => dispatch({ type: "RESET_CASHOUT_ERROR" })}
        handleAction={() => dispatch({ type: "RESET_CASHOUT_ERROR" })}
        handleCancel={() => dispatch({ type: "RESET_CASHOUT_ERROR" })}
      />
      <Alert
        title="Ops, insufficient balance for Cash Out"
        description="Your financial goal has insufficient balance to cash out. The minimum cash out limit is RM10."
        isOpen={state.isFullRedemptionError}
        isCancel={false}
        handleClose={() => dispatch({ type: "RESET_FULL_REDEMPTION_ERROR" })}
        handleAction={() => dispatch({ type: "RESET_FULL_REDEMPTION_ERROR" })}
        handleCancel={() => dispatch({ type: "RESET_FULL_REDEMPTION_ERROR" })}
      />
      <Alert
        title="Ops, minimum financial goal balance limit"
        description="Production allocation must be equal or above minimum amount."
        isOpen={state.isLowBalanceError}
        isCancel={false}
        handleClose={() => dispatch({ type: "RESET_LOW_BALANCE_ERROR" })}
        handleAction={() => dispatch({ type: "RESET_LOW_BALANCE_ERROR" })}
        handleCancel={() => dispatch({ type: "RESET_LOW_BALANCE_ERROR" })}
      />

      <StepLabel
        title="Cash Out"
        containerStyles={{ margin: "16px 16px 22px" }}
        onClick={handleBack}
      />
      <Form>
        <Typography
          color="textPrimary"
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: 1.43,
            marginBottom: "8px",
          }}
        >
          Please enter an amount
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="0.00"
          value={state.withdrawalAmount}
          onChange={e =>
            dispatch({ type: "SET_AMOUNT", payload: e.target.value })
          }
          error={
            state.withdrawalAmount &&
            (state.withdrawalAmount < state.saveMinAmount ||
              state.withdrawalAmount < state.investMinAmount)
          }
          helperText={
            state.withdrawalAmount &&
            (state.withdrawalAmount < state.saveMinAmount ||
              state.withdrawalAmount < state.investMinAmount) &&
            "Minimum RM 10.00"
          }
          FormHelperTextProps={{
            style: {
              margin: "4px 0 0 0",
              color: "#be0712",
              fontSize: "14px",
              lineHeight: 1.57,
            },
          }}
          inputProps={{
            style: {
              padding: "12px 16px 12px 0",
              borderColor: "rgba(51, 51, 51, 0.2)",
            },
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={{
            inputComponent: NumberFormatCustom,
            style: { paddingLeft: "16px" },
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  color="textPrimary"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  RM
                </Typography>
              </InputAdornment>
            ),
            endAdornment: (
              <div
                onClick={handleFullRedemption}
                style={{
                  border: "1px solid #0091DA",
                  borderRadius: "4px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "#0091DA",
                    width: "100px",
                    textAlign: "center",
                    padding: "2px",
                    fontSize: "14px",
                  }}
                >
                  Max Amount
                </Typography>
              </div>
            ),
          }}
        />
      </Form>
      <CashOutInfo>
        {isSave && (
          <li>
            <span>•</span>{" "}
            <p>
              <span style={{ fontWeight: "700" }}>Save feature:</span> Cash Out
              request for all financial goals and general savings will take up
              to 2 business days (T+2) to be processed.
            </p>
          </li>
        )}
        {isInvest && (
          <li>
            <p>
              <span style={{ fontWeight: "700" }}>Invest feature:</span> Cash
              outs are processed within 7 business days (T+7), or up to 15
              business days for foreign feeder funds. Final value follows the
              NAV selling price on your cash-out date.
            </p>
          </li>
        )}
      </CashOutInfo>
      <ButtonWrapper
        style={{
          backgroundColor: "#fff",
        }}
      >
        <CustomButton
          text="Cash Out"
          variant="contained"
          color="#fff"
          backgroundColor="#1278CC"
          disabled={
            state.withdrawalAmount < state.saveMinAmount ||
            state.withdrawalAmount < state.investMinAmount
          }
          onClick={() => dispatch({ type: "SET_OPEN_ALERT" })}
        />
      </ButtonWrapper>
    </Container>
  );
};

const withConnect = connect(
  state => ({
    loading: state.goal.loading,
    savingType: state.goal.savingType,
    savingGoalDetails: state.goal.savingGoalDetails,
    generalSavingDetails: state.goal.generalSavingDetails,
    customerGoals: state.goal.customerGoals,
    maxAmount: state.goal.maxAmount,
    loadingMaxAmount: state.goal.loadingMaxAmount,
    fund: state.fundSelectionReducer.fundsCode[0],
  }),
  {
    checkWithdrawalMaxAmount,
    sendFundsCode,
    setFullRedemption,
  }
);

export default compose(
  withRouter,
  withConnect
)(MakeWithdrawal);
